import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Inbox,
  MessageSquare,
  Users,
  Sparkles,
  HandHelping,
  FileText,
  Link2,
  Bot,
  Activity,
  RefreshCw,
  TrendingUp,
  ShieldCheck,
  ShieldX,
  Tag,
  Send,
} from 'lucide-react'
import { api } from '@/lib/api'
import { MetricCard } from '@/components/marketing/MetricCard'

interface KpiResponse {
  window: { days: number; fromIso: string }
  conversations: { open: number; snoozed: number; closed: number; totalLastWindow: number }
  messages: {
    inbound: number
    outbound: number
    byChannel: { channel: string; count: number }[]
  }
  clients: { total: number; newInWindow: number; withActivityInWindow: number }
  bots: {
    suggestionsGenerated: number
    approvedCount: number
    editedCount: number
    rejectedCount: number
    autoSentCount: number
    avgConfidence: number | null
    handoffsOpen: number
    handoffsCreated: number
  }
  forms: { submissionsInWindow: number; top5: { formName: string; count: number }[] }
  triggerLinks: {
    clicksInWindow: number
    top5: { slug: string; label: string | null; clicks: number }[]
  }
  webhooks: { successRate: number | null; deliveriesInWindow: number; paused: number }
  activityTimeseries: { date: string; inbound: number; outbound: number }[]
  // Endpoint público sin auth → backend devuelve sólo la inicial del cliente
  // y un resumen genérico sin preview, por minimización RGPD art. 5(1)(c).
  recentActivity: { type: string; occurredAt: string; clientInitial: string; summary: string }[]
}

const WINDOWS = [
  { days: 7, label: '7d' },
  { days: 14, label: '14d' },
  { days: 30, label: '30d' },
]

// Paleta canales para el donut — mantenemos consistencia con bp tokens.
const CHANNEL_COLORS: Record<string, string> = {
  EMAIL: '#1B4FD8',
  SMS: '#0EA5E9',
  WHATSAPP: '#22C55E',
  VOICE: '#A855F7',
  INSTAGRAM: '#EC4899',
  FACEBOOK: '#3B82F6',
  WEBCHAT: '#F59E0B',
}
const FALLBACK_COLORS = ['#475569', '#94A3B8', '#CBD5E1']

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  MESSAGE_INBOUND: MessageSquare,
  MESSAGE_OUTBOUND: Send,
  TAG_ADDED: Tag,
  TAG_REMOVED: Tag,
  BOT_SUGGESTION_GENERATED: Sparkles,
  BOT_HANDOFF_REQUESTED: HandHelping,
  BOT_HANDOFF_CLAIMED: HandHelping,
  BOT_HANDOFF_RESOLVED: HandHelping,
  SUGGESTION_APPROVED: ShieldCheck,
  SUGGESTION_EDITED_AND_SENT: ShieldCheck,
  SUGGESTION_REJECTED: ShieldX,
  SHADOW_REVIEW_CREATED: Sparkles,
  CONSENT_GIVEN: ShieldCheck,
  CONSENT_WITHDRAWN: ShieldX,
  CONVERSATION_OPENED: Inbox,
}

function colorForChannel(channel: string, idx: number): string {
  return CHANNEL_COLORS[channel] ?? FALLBACK_COLORS[idx % FALLBACK_COLORS.length] ?? '#475569'
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'ahora'
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d`
}

export function DashboardPage() {
  const [days, setDays] = useState<number>(7)
  const [data, setData] = useState<KpiResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function load(currentDays = days) {
    setLoading(true)
    setErr(null)
    try {
      const res = await api<KpiResponse>(`/v1/marketing/dashboard/kpis?days=${currentDays}`)
      setData(res)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load(days)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days])

  return (
    <div className="p-6 max-w-7xl">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-medium tracking-tight">Dashboard marketing</h1>
          <p className="text-xs font-mono text-ink-700/60">
            KPIs últimos {days} días · single tenant · datos en vivo
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded border border-ink-900/10 overflow-hidden">
            {WINDOWS.map((w) => (
              <button
                key={w.days}
                onClick={() => setDays(w.days)}
                className={[
                  'px-3 py-1.5 text-xs font-mono',
                  days === w.days
                    ? 'bg-blueprint-600 text-white'
                    : 'bg-white text-ink-700/70 hover:bg-surface-alt',
                ].join(' ')}
              >
                {w.label}
              </button>
            ))}
          </div>
          <button className="bp-btn" onClick={() => void load()} disabled={loading}>
            <RefreshCw className="size-3.5" />
            {loading ? 'Cargando…' : 'Refrescar'}
          </button>
        </div>
      </header>

      {err && (
        <div className="bp-card p-3 mb-4 text-sm font-mono text-red-700 border-red-300 bg-red-50">
          {err}
        </div>
      )}

      {!data && !err && !loading && (
        <div className="bp-card p-8 text-center text-sm text-ink-700/60">
          Sin datos para mostrar.
        </div>
      )}

      {data && <DashboardBody data={data} />}
    </div>
  )
}

function DashboardBody({ data }: { data: KpiResponse }) {
  const totalMsgs = data.messages.inbound + data.messages.outbound
  return (
    <>
      {/* Fila KPI principal */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <MetricCard
          label="Conv. abiertas"
          value={data.conversations.open}
          hint={`${data.conversations.snoozed} pausadas`}
          icon={<Inbox className="size-3.5" />}
          accent="blueprint"
        />
        <MetricCard
          label={`Mensajes ${data.window.days}d`}
          value={totalMsgs}
          hint={`${data.messages.inbound} in · ${data.messages.outbound} out`}
          icon={<MessageSquare className="size-3.5" />}
        />
        <MetricCard
          label="Clientes activos"
          value={data.clients.withActivityInWindow}
          hint={`${data.clients.newInWindow} nuevos`}
          icon={<Users className="size-3.5" />}
        />
        <MetricCard
          label="Sugerencias IA"
          value={data.bots.suggestionsGenerated}
          hint={
            data.bots.avgConfidence !== null
              ? `confianza media ${(data.bots.avgConfidence * 100).toFixed(0)}%`
              : 'sin datos'
          }
          icon={<Sparkles className="size-3.5" />}
        />
        <MetricCard
          label="Handoffs abiertos"
          value={data.bots.handoffsOpen}
          hint={`${data.bots.handoffsCreated} en ventana`}
          icon={<HandHelping className="size-3.5" />}
          accent={data.bots.handoffsOpen > 0 ? 'amber' : 'default'}
        />
        <MetricCard
          label={`Forms ${data.window.days}d`}
          value={data.forms.submissionsInWindow}
          hint={`${data.triggerLinks.clicksInWindow} clicks links`}
          icon={<FileText className="size-3.5" />}
        />
      </div>

      {/* Quick links */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Link
          to="/marketing/handoffs"
          className="bp-card px-3 py-2 flex items-center gap-2 text-sm hover:bg-surface-alt"
        >
          <HandHelping className="size-4 text-amber-600" />
          <span>Handoffs</span>
          {data.bots.handoffsOpen > 0 && (
            <span className="bp-pill bg-amber-500 text-white">{data.bots.handoffsOpen}</span>
          )}
        </Link>
        <Link
          to="/marketing/inbox"
          className="bp-card px-3 py-2 flex items-center gap-2 text-sm hover:bg-surface-alt"
        >
          <Inbox className="size-4 text-blueprint-700" />
          <span>Inbox</span>
          {data.conversations.open > 0 && (
            <span className="bp-pill bg-blueprint-600 text-white">{data.conversations.open}</span>
          )}
        </Link>
        <Link
          to="/marketing/shadow-review"
          className="bp-card px-3 py-2 flex items-center gap-2 text-sm hover:bg-surface-alt"
        >
          <Sparkles className="size-4 text-blueprint-700" />
          <span>Shadow review</span>
        </Link>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div className="bp-card p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="size-4 text-blueprint-700" />
              Mensajes por día
            </h2>
            <span className="text-[11px] font-mono text-ink-700/60">
              inbound vs outbound · últimos {data.window.days}d
            </span>
          </div>
          <BarChartStacked series={data.activityTimeseries} />
        </div>
        <div className="bp-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium flex items-center gap-2">
              <Activity className="size-4 text-blueprint-700" />
              Por canal
            </h2>
            <span className="text-[11px] font-mono text-ink-700/60">{totalMsgs} msg</span>
          </div>
          <DonutChannels byChannel={data.messages.byChannel} />
        </div>
      </div>

      {/* Tops + recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bp-card p-4">
          <h2 className="text-sm font-medium flex items-center gap-2 mb-2">
            <FileText className="size-4 text-blueprint-700" />
            Top forms
          </h2>
          {data.forms.top5.length === 0 ? (
            <div className="text-xs font-mono text-ink-700/60">Sin envíos en la ventana.</div>
          ) : (
            <ul className="space-y-1">
              {data.forms.top5.map((f) => (
                <li
                  key={f.formName}
                  className="flex items-center justify-between text-sm border-b border-ink-900/5 last:border-0 py-1"
                >
                  <span className="truncate pr-2">{f.formName}</span>
                  <span className="bp-pill">{f.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bp-card p-4">
          <h2 className="text-sm font-medium flex items-center gap-2 mb-2">
            <Link2 className="size-4 text-blueprint-700" />
            Top trigger links
          </h2>
          {data.triggerLinks.top5.length === 0 ? (
            <div className="text-xs font-mono text-ink-700/60">Sin clicks en la ventana.</div>
          ) : (
            <ul className="space-y-1">
              {data.triggerLinks.top5.map((t) => (
                <li
                  key={t.slug}
                  className="flex items-center justify-between text-sm border-b border-ink-900/5 last:border-0 py-1"
                >
                  <span className="truncate pr-2">
                    {t.label ?? <span className="font-mono">/{t.slug}</span>}
                  </span>
                  <span className="bp-pill">{t.clicks}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bp-card p-4">
          <h2 className="text-sm font-medium flex items-center gap-2 mb-2">
            <Activity className="size-4 text-blueprint-700" />
            Actividad reciente
          </h2>
          {data.recentActivity.length === 0 ? (
            <div className="text-xs font-mono text-ink-700/60">Sin actividad.</div>
          ) : (
            <ul className="space-y-1 max-h-72 overflow-auto">
              {data.recentActivity.map((a, idx) => {
                const Icon = ICONS[a.type] ?? Activity
                return (
                  <li
                    key={`${a.occurredAt}-${idx}`}
                    className="flex items-start gap-2 text-xs py-1 border-b border-ink-900/5 last:border-0"
                  >
                    <Icon className="size-3.5 text-ink-700/50 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className="font-medium truncate"
                          title="Inicial — datos minimizados (RGPD art. 5)"
                        >
                          Cliente {a.clientInitial}.
                        </span>
                        <span className="font-mono text-[10px] text-ink-700/50 shrink-0">
                          {timeAgo(a.occurredAt)}
                        </span>
                      </div>
                      <div className="text-ink-700/70 truncate">{a.summary}</div>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
        <div className="bp-card p-4">
          <h2 className="text-sm font-medium flex items-center gap-2 mb-2">
            <Bot className="size-4 text-blueprint-700" />
            Calidad bot
          </h2>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Stat label="Aprobadas" value={data.bots.approvedCount} />
            <Stat label="Editadas" value={data.bots.editedCount} />
            <Stat label="Rechazadas" value={data.bots.rejectedCount} />
            <Stat label="Autopilot" value={data.bots.autoSentCount} />
          </div>
        </div>
        <div className="bp-card p-4">
          <h2 className="text-sm font-medium flex items-center gap-2 mb-2">
            <Link2 className="size-4 text-blueprint-700" />
            Webhooks
          </h2>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Stat
              label="Tasa éxito"
              value={
                data.webhooks.successRate === null
                  ? '—'
                  : `${(data.webhooks.successRate * 100).toFixed(0)}%`
              }
            />
            <Stat label="Entregas" value={data.webhooks.deliveriesInWindow} />
            <Stat
              label="Pausados"
              value={data.webhooks.paused}
              accent={data.webhooks.paused > 0 ? 'red' : undefined}
            />
          </div>
        </div>
        <div className="bp-card p-4">
          <h2 className="text-sm font-medium flex items-center gap-2 mb-2">
            <Users className="size-4 text-blueprint-700" />
            Clientes
          </h2>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Stat label="Total" value={data.clients.total} />
            <Stat label="Nuevos" value={data.clients.newInWindow} />
            <Stat label="Activos" value={data.clients.withActivityInWindow} />
            <Stat label="Conv. nuevas" value={data.conversations.totalLastWindow} />
          </div>
        </div>
      </div>
    </>
  )
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string
  value: number | string
  accent?: 'red'
}) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
        {label}
      </span>
      <span
        className={[
          'text-base font-medium',
          accent === 'red' ? 'text-red-700' : 'text-ink-900',
        ].join(' ')}
      >
        {value}
      </span>
    </div>
  )
}

// ----- Charts (SVG inline) -----

interface BarChartStackedProps {
  series: { date: string; inbound: number; outbound: number }[]
}

/**
 * Bar chart apilado inbound (azul oscuro) + outbound (azul claro).
 * SVG manual: cada barra es un grupo con dos rects apilados, todo escalado
 * al máximo del eje Y. No usamos librería.
 */
function BarChartStacked({ series }: BarChartStackedProps) {
  const width = 640
  const height = 180
  const padding = { left: 28, right: 8, top: 8, bottom: 24 }
  const innerW = width - padding.left - padding.right
  const innerH = height - padding.top - padding.bottom
  const max = useMemo(
    () => Math.max(1, ...series.map((s) => s.inbound + s.outbound)),
    [series],
  )
  const n = Math.max(series.length, 1)
  const barGap = 4
  const barW = Math.max(2, innerW / n - barGap)

  const yTicks = useMemo(() => {
    const ticks: number[] = []
    const niceMax = niceCeil(max)
    for (let i = 0; i <= 4; i++) {
      ticks.push((niceMax / 4) * i)
    }
    return { ticks, niceMax }
  }, [max])

  // Etiquetas X: sólo primer, mitad, último — para no apilar texto en 30d.
  const labelIdxs = new Set<number>([0, Math.floor(n / 2), n - 1])

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label="Mensajes por día"
      >
        {/* eje Y + grid */}
        {yTicks.ticks.map((t, i) => {
          const y = padding.top + innerH - (t / yTicks.niceMax) * innerH
          return (
            <g key={i}>
              <line
                x1={padding.left}
                x2={width - padding.right}
                y1={y}
                y2={y}
                stroke="#E5E7EB"
                strokeWidth="1"
              />
              <text
                x={padding.left - 4}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize="9"
                fill="#64748B"
                fontFamily="ui-monospace, monospace"
              >
                {Math.round(t)}
              </text>
            </g>
          )
        })}

        {series.map((s, i) => {
          const x = padding.left + i * (innerW / n) + barGap / 2
          const total = s.inbound + s.outbound
          const totalH = (total / yTicks.niceMax) * innerH
          const inH = (s.inbound / yTicks.niceMax) * innerH
          const outH = (s.outbound / yTicks.niceMax) * innerH
          const yTop = padding.top + innerH - totalH
          return (
            <g key={s.date}>
              {/* inbound abajo */}
              <rect
                x={x}
                y={padding.top + innerH - inH}
                width={barW}
                height={inH}
                fill="#1B4FD8"
                rx="2"
              />
              {/* outbound encima */}
              <rect
                x={x}
                y={yTop}
                width={barW}
                height={outH}
                fill="#93C5FD"
                rx="2"
              />
              {labelIdxs.has(i) && (
                <text
                  x={x + barW / 2}
                  y={height - 8}
                  textAnchor="middle"
                  fontSize="9"
                  fill="#64748B"
                  fontFamily="ui-monospace, monospace"
                >
                  {s.date.slice(5)}
                </text>
              )}
            </g>
          )
        })}
      </svg>
      <div className="flex gap-3 text-[11px] font-mono text-ink-700/60 mt-1">
        <span className="flex items-center gap-1">
          <span
            className="inline-block size-2 rounded-sm"
            style={{ backgroundColor: '#1B4FD8' }}
          />
          inbound
        </span>
        <span className="flex items-center gap-1">
          <span
            className="inline-block size-2 rounded-sm"
            style={{ backgroundColor: '#93C5FD' }}
          />
          outbound
        </span>
      </div>
    </div>
  )
}

function niceCeil(n: number): number {
  if (n <= 1) return 1
  const pow = Math.pow(10, Math.floor(Math.log10(n)))
  const norm = n / pow
  let nice: number
  if (norm <= 1) nice = 1
  else if (norm <= 2) nice = 2
  else if (norm <= 5) nice = 5
  else nice = 10
  return nice * pow
}

/**
 * Donut chart por canal. SVG inline: usamos un círculo lleno como fondo
 * y arcos calculados con stroke-dasharray sobre un círculo único, alineado
 * con el porcentaje acumulado de cada slice. Más simple y ligero que dibujar
 * paths con arcsTo.
 */
function DonutChannels({
  byChannel,
}: {
  byChannel: { channel: string; count: number }[]
}) {
  const total = byChannel.reduce((acc, b) => acc + b.count, 0)
  const size = 160
  const radius = 60
  const strokeWidth = 22
  const circumference = 2 * Math.PI * radius
  const cx = size / 2
  const cy = size / 2

  if (total === 0) {
    return (
      <div className="flex flex-col items-center text-xs font-mono text-ink-700/60 py-6">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth={strokeWidth}
          />
        </svg>
        Sin mensajes en la ventana.
      </div>
    )
  }

  let cumulative = 0
  return (
    <div className="flex flex-col items-center gap-3">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label="Mensajes por canal"
      >
        {/* Track gris de fondo */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="#F1F5F9"
          strokeWidth={strokeWidth}
        />
        {byChannel.map((b, i) => {
          const fraction = b.count / total
          const dash = fraction * circumference
          const gap = circumference - dash
          // Rotamos cada arco para que empiece donde terminó el anterior.
          const rotation = (cumulative / total) * 360 - 90
          cumulative += b.count
          return (
            <circle
              key={b.channel}
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke={colorForChannel(b.channel, i)}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dash} ${gap}`}
              transform={`rotate(${rotation} ${cx} ${cy})`}
            />
          )
        })}
        <text
          x={cx}
          y={cy - 2}
          textAnchor="middle"
          fontSize="20"
          fontWeight="500"
          fill="#0F172A"
        >
          {total}
        </text>
        <text
          x={cx}
          y={cy + 14}
          textAnchor="middle"
          fontSize="9"
          fill="#64748B"
          fontFamily="ui-monospace, monospace"
        >
          mensajes
        </text>
      </svg>
      <ul className="text-[11px] font-mono w-full space-y-0.5">
        {byChannel.map((b, i) => (
          <li key={b.channel} className="flex items-center gap-2">
            <span
              className="inline-block size-2 rounded-sm shrink-0"
              style={{ backgroundColor: colorForChannel(b.channel, i) }}
            />
            <span className="flex-1 truncate">{b.channel}</span>
            <span className="text-ink-700/60">
              {b.count} · {((b.count / total) * 100).toFixed(0)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
